import { useState, forwardRef, useRef, useEffect, memo } from "react";
import {
  DropDownMenu,
  InfiniteScrollGrid,
  SplitButton
} from "@etframework/ui.components";
import { ColumnDirective } from "@syncfusion/ej2-react-grids";
import emptyStateImg from "../../content/img/tasktemplate-empty-state.png";
import { AppDataManager } from "../../utils/dataManager";
import { useTranslation } from "react-i18next";
import useModalEvent from "../../hooks/useModalEvent";
import useEvent from "../../hooks/useEvent";
import { useAuth } from "oidc-react";
import Enums from "../../types/enums";

const ShowDropDown = ({ gridRef }) => {
  const { t } = useTranslation(["TaskTypes"]);
  const [active, setActive] = useState(true);

  const dropdownOptions = [
    {
      id: "et-task-templategrid-dropdown-active",
      text: t("Active"),
      iconCss: active ? "fa-solid fa-check" : "",
      onClick: () => {
        gridRef?.current
          ?.getComponentRef()
          ?.filterByColumn("IsActive", "equal", true);
        setActive(true);
      }
    },
    {
      id: "et-task-templategrid-dropdown-inactive",
      text: t("Inactive"),
      iconCss: !active ? "fa-solid fa-check" : "",
      onClick: () => {
        gridRef?.current
          ?.getComponentRef()
          ?.filterByColumn("IsActive", "equal", false);
        setActive(false);
      }
    }
  ];

  return (
    <div className="d-flex justify-content-start align-items-center gap-2">
      <div className="" style={{ fontSize: "14px" }}>
        {t("Show")}
      </div>
      <div>
        <DropDownMenu
          id={"et-task-templategrid-show-dropdown"}
          select={(args) => args.onClick?.()}
          items={dropdownOptions}
        >
          <div className="fw-600">{active ? t("Active") : t("Inactive")}</div>
        </DropDownMenu>
      </div>
    </div>
  );
};

export const TaskTemplateGrid = memo(
  forwardRef(({ id, taskTypeId }, ref) => {
    const { t, i18n } = useTranslation(["TaskTypes"]);
    const clickedTaskTemplateRef = useRef(null);
    const { userManager } = useAuth();
    const dataManager = new AppDataManager(
      `tasktemplates/grid/tasktype/${taskTypeId}`,
      userManager.signinSilent.bind(userManager)
    );
    const { ModalEvents, toggleModal } = useModalEvent();
    const { Events, useRegisterCustomEvent, toggleEvent } = useEvent();

    const nameTemplate = (props) => {
      const handleTaskTemplateNameButtonClick = () => {
        toggleEvent(Events.UpdateTaskTemplate, {
          templateId: props?.Id
        });
      };

      return (
        <span
          onClick={handleTaskTemplateNameButtonClick}
          className="et-tasktemplategrid-name"
        >
          {props.Name}
        </span>
      );
    };

    const addTemplateButton = () => {
      const items = [
        {
          iconCss: "fa-regular fa-download fs-14 et-dark-gray",
          text: t("TaskTemplateGrid.ImportTemplates"),
          onClick: () =>
            toggleModal(ModalEvents.ImportTemplates, {
              taskTypeId: taskTypeId
            })
        },
        {
          text: t("TaskTemplateGrid.ViewImportLog"),
          iconCss: "fs-14 fa-regular fa-clipboard-list-check et-dark-gray",
          onClick: () =>
            toggleModal(Events.TaskTemplatesImportLogOverlay, {
              taskTypeId: taskTypeId
            })
        }
      ];

      const select = (args) => {
        args?.item?.onClick?.();
      };

      return (
        <SplitButton
          onClick={() => {
            toggleModal(ModalEvents.TaskTemplateModal);
          }}
          id={id + "_toolbar_add-button"}
          iconCss={"fa-regular fa-chevron fs-12"}
          className="split-btn"
          {...{ items, select }}
        >
          {<i className="fa-regular fa-plus me-2"></i>}
          {t("TaskTemplateGrid.Add")}
        </SplitButton>
      );
    };

    const getSpecificDueDate = (props) => {
      const {
        DateFrequency,
        DueDateRuleInfoMonth,
        DueDateRuleInfoDay,
        FinancialYear
      } = props;

      const fiscalYear =
        FinancialYear == Enums.FinancialYear.CalendarYear
          ? t("TaskTemplateGrid.CalendarYear")
          : t("TaskTemplateGrid.FiscalYear");

      if (DateFrequency != null || FinancialYear != null) {
        const dateFrequency = t(`TaskTemplateGrid.Frequency.${DateFrequency}`);
        const message = t(`TaskTemplateGrid.Message.${DateFrequency}`, {
          month: getOrdinalSuffix(DueDateRuleInfoMonth),
          day: getOrdinalSuffix(DueDateRuleInfoDay),
          year: fiscalYear
        });
        return { dateFrequency, message };
      }
      return null;
    };

    const specificDueDateTemplate = (props) => {
      const specificDuedate = getSpecificDueDate(props);
      if (specificDuedate) {
        return (
          <div>
            <strong>{specificDuedate.dateFrequency}</strong>
            {specificDuedate.message}
          </div>
        );
      } else {
        return <span>-</span>;
      }
    };

    const DueDateFilterItemTemplate = (props) => {
      if (props.SpecificDueDate) {
        const values = props?.SpecificDueDate.split("-");
        const { dateFrequency, message } = getSpecificDueDate({
          DateFrequency: values[0],
          DueDateRuleInfoMonth: values[1],
          DueDateRuleInfoDay: values[2],
          FinancialYear: values[3]
        });
        return `${dateFrequency} ${message}`;
      } else {
        return "-";
      }
    };

    const extensionRuleTemplate = (props) => {
      const { DueDateExtensionRuleInfoMonth } = props;

      let message = "-";

      if (DueDateExtensionRuleInfoMonth != null) {
        message = `${getOrdinalSuffix(DueDateExtensionRuleInfoMonth)} ${t(
          "TaskTemplateGrid.MonthAfter"
        )}`;
      }

      return <span>{message}</span>;
    };

    const getOrdinalSuffix = (number) => {
      if (!number) return null;

      if (number == 31) return t("TaskTemplateGrid.LastDay");

      const suffixes = [
        t("TaskTemplateGrid.th"),
        t("TaskTemplateGrid.st"),
        t("TaskTemplateGrid.nd"),
        t("TaskTemplateGrid.rd")
      ];
      const value = number % 100;
      return `${number}${
        suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]
      }`;
    };

    const toolbarCommonOptions = {
      align: "Left",
      type: "Button"
    };

    const AddTaskTemplate = {
      ...toolbarCommonOptions,
      align: "Right",
      visible: true,
      enabled: true,
      template: addTemplateButton
    };

    const EditTaskTemplate = {
      ...toolbarCommonOptions,
      id: id + "_toolbar_edit-button",
      visible: false,
      text: t("TaskTemplateGrid.Edit"),
      prefixIcon: "fa-regular fa-pencil",
      click: () => {
        toggleEvent(Events.UpdateTaskTemplate, {
          templateId: clickedTaskTemplateRef.current?.Id
        });
      },
      enabled: true
    };

    const DeleteTaskTemplate = {
      ...toolbarCommonOptions,
      id: id + "_toolbar_delete-button",
      visible: false,
      text: t("TaskTemplateGrid.Delete"),
      prefixIcon: "fa-regular fa-trash-can",
      click: () => {
        const selectedRows = ref?.current
          ?.getComponentRef()
          ?.getSelectedRecords();

        toggleModal(ModalEvents.DeleteTaskTemplateModal, selectedRows);
      },
      enabled: true
    };

    const toolbarItems = [
      EditTaskTemplate,
      DeleteTaskTemplate,
      AddTaskTemplate
    ];

    const searchSettings = {
      fields: ["Name"],
      operator: "contains",
      ignoreCase: true
    };

    const onRowSelected = (_, selectedCount) => {
      const selectedRows = ref?.current.getComponentRef().getSelectedRecords();
      const everySelectionIsACustomTemplate = selectedRows.every(
        (row) => !row.Locked
      );

      toolbarItems.forEach((item) => {
        switch (item?.id) {
          case AddTaskTemplate.id:
            item.enabled = true;
            break;
          case EditTaskTemplate.id:
            item.enabled = selectedCount === 1;
            break;
          case DeleteTaskTemplate.id:
            item.enabled = everySelectionIsACustomTemplate;
            break;
          default:
            break;
        }
      });

      let itemsToShow = [];

      itemsToShow = toolbarItems
        ?.filter((item) => item.enabled)
        .map((item) => item?.id);

      ref?.current?.setToolbarItemVisibility(itemsToShow, true, true);
    };

    const onRowDeselected = (args, selectedCount) => {
      if (selectedCount === 0)
        ref?.current?.setToolbarItemVisibility(
          ["search-input", AddTaskTemplate?.id],
          true,
          true
        );
      else onRowSelected(args, selectedCount);
    };

    const emptyTasksTemplate = () => {
      return (
        <div className="et-tasktemplate-empty-state">
          <img src={emptyStateImg} />
          <div className="et-empty-state-msg">
            <span>{t("TaskTemplateGrid.EmptyStateMsg1")}</span>
            <span>{t("TaskTemplateGrid.EmptyStateMsg2")}</span>
          </div>
        </div>
      );
    };

    const refreshTaskTemplateGrid = () => {
      ref?.current?.getComponentRef()?.refresh();
    };

    useEffect(() => {
      ref?.current?.getComponentRef().refreshHeader();
    }, [i18n.language]);

    useRegisterCustomEvent(
      Events.RefreshTaskTemplateGrid,
      refreshTaskTemplateGrid
    );

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3 mt-1 et-tasktemplategrid-dropdown-container gap-2">
          <div className="et-darkest-gray fs-14">
            {t("TaskTemplateGrid.Title")}
          </div>
          <div className="divider" />
          <ShowDropDown gridRef={ref} />
        </div>
        <InfiniteScrollGrid
          ref={ref}
          id={id}
          autoHeight={true}
          className="et-tasktemplategrid"
          showHeader={false}
          dataSource={dataManager}
          filterSettings={{
            type: "Menu",
            columns: [
              {
                field: "IsActive",
                matchCase: false,
                operator: "equal",
                predicate: "and",
                value: true
              }
            ]
          }}
          allowResizing={true}
          recordDoubleClick={(args) => {
            toggleEvent(Events.UpdateTaskTemplate, {
              templateId: args.rowData?.Id
            });
          }}
          emptyRecordTemplate={emptyTasksTemplate}
          toolbarItems={toolbarItems}
          rowSelected={onRowSelected}
          rowDeselected={onRowDeselected}
          recordClick={(args) => {
            clickedTaskTemplateRef.current = args.rowData;
          }}
          searchSettings={searchSettings}
          allowFiltering={true}
          language={i18n.language}
        >
          <ColumnDirective field="IsActive" type="boolean" visible={false} />
          <ColumnDirective
            field="Name"
            type="string"
            headerText={t("TaskTemplateGrid.Name")}
            template={nameTemplate}
            filter={{
              type: "CheckBox"
            }}
          />
          <ColumnDirective
            field="SpecificDueDate"
            type="string"
            headerText={t("TaskTemplateGrid.DueDate")}
            template={specificDueDateTemplate}
            filter={{
              itemTemplate: DueDateFilterItemTemplate
            }}
          />
          <ColumnDirective
            field="DueDateExtensionRuleInfoMonth"
            type="string"
            headerText={t("TaskTemplateGrid.ExtensionRule")}
            template={extensionRuleTemplate}
            allowReordering={false}
            filter={{
              type: "CheckBox"
            }}
          />
        </InfiniteScrollGrid>
      </div>
    );
  })
);
